import { useMemo } from "react"
import type { BinaryOperator, Filter, Query } from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useDepVar from "~/ui-rtk/hooks/dep-var"

const PREFIX = "company_media_metrics_ads_creative_report"

export type TCreativeTrendPoint = {
  date: string
  spend: number
  brandValue: number
  purchases: number
}

/**
 * Daily spend / brand value trend for a single creative report card.
 * Filters the creative report cube by the report mode's primary id (e.g.
 * root_ad_name, creative_id) and, in grouped mode, the card's media channel.
 */
export default function useCreativeTrendQuery({
  primaryIdKey,
  primaryIdValue,
  mediaChannel,
  dateRange,
}: {
  primaryIdKey: string
  primaryIdValue: string
  mediaChannel?: string
  dateRange: [string, string]
}) {
  const { depVar, depVarScaler } = useDepVar()
  const depVarScalerKey = depVarScaler.join(",")
  const [startDate, endDate] = dateRange

  const query = useMemo<Query>(() => {
    const filters: Filter[] = [
      {
        member: `${PREFIX}.dep_var`,
        operator: "equals" as BinaryOperator,
        values: [depVar],
      },
      {
        member: `${PREFIX}.${primaryIdKey}`,
        operator: "equals" as BinaryOperator,
        values: [primaryIdValue],
      },
    ]
    if (depVarScaler.length > 0) {
      filters.push({
        member: `${PREFIX}.dep_var_scaler`,
        operator: "equals" as BinaryOperator,
        values: depVarScaler,
      })
    }
    if (mediaChannel) {
      filters.push({
        member: `${PREFIX}.media_channel`,
        operator: "equals" as BinaryOperator,
        values: [mediaChannel],
      })
    }

    return {
      measures: [
        `${PREFIX}.spend`,
        `${PREFIX}.brand_value_agg`,
        `${PREFIX}.purchases`,
      ],
      timeDimensions: [
        {
          dimension: `${PREFIX}.md_date`,
          granularity: "day",
          dateRange: [startDate, endDate],
        },
      ],
      filters,
      order: { [`${PREFIX}.md_date`]: "asc" },
    }
  }, [
    primaryIdKey,
    primaryIdValue,
    mediaChannel,
    startDate,
    endDate,
    depVar,
    depVarScalerKey,
  ])

  const { resultSet, isLoading, error } = useCubeQuery(query, {
    skip: !primaryIdValue,
  })

  const trend = useMemo<TCreativeTrendPoint[]>(() => {
    if (!resultSet) return []
    return resultSet.rawData().map(row => ({
      date: String(
        row[`${PREFIX}.md_date.day`] ?? row[`${PREFIX}.md_date`] ?? "",
      ),
      spend: Number(row[`${PREFIX}.spend`] ?? 0),
      brandValue: Number(row[`${PREFIX}.brand_value_agg`] ?? 0),
      purchases: Number(row[`${PREFIX}.purchases`] ?? 0),
    }))
  }, [resultSet])

  return { trend, isLoading, error }
}
